// PRE RMA — Hors-ligne des modules (PEV, intrants, finance, décès, communication, pré-inventaire, inventaire).
//
// Principe « local d'abord » : chaque saisie est écrite IMMÉDIATEMENT dans IndexedDB (l'écran ne dépend jamais du réseau),
// puis une opération est placée dans une file d'attente et envoyée UNE PAR UNE (une requête = une opération, jamais de lot : bas débit 2G/3G)
// dès que le réseau le permet. Une opération REFUSÉE par le serveur (validation métier) est conservée localement, marquée « Refusé / À vérifier »,
// et ne bloque jamais les suivantes.
//   - 1 saisie = 1 opération ; plusieurs frappes sur la même ligne = 1 seule opération (coalescence) ;
//   - verrouillage optimiste : chaque opération porte la version serveur connue (baseVersion) ;
//   - conflit : la version du serveur est CONSERVÉE, la version locale est tracée pour l'utilisateur (jamais d'écrasement silencieux) ;
//   - rejeu sans danger : opId unique par opération, le serveur ignore un rejeu.
import { api } from '../api/client.ts';
import type { MedicamentCatalogue, SaisieModule, ReportsModules } from '../../../shared/business-logic/index.ts';
import { nettoyerOperationModule } from '../../../shared/business-logic/nettoyage.ts';

const DB_NOM = 'prerma-modules';
const DB_VERSION = 1;

export interface LigneSnap extends SaisieModule { version: number; id?: string; enAttente?: boolean }
export interface PeriodeSnap { id: string; csbId: string; annee: number; mois: number; statut: string; csbNom: string }
export interface Snap {
  periodeId: string; periode: PeriodeSnap; saisies: LigneSnap[]; reports: ReportsModules; pevAnnee: Array<any | null>; majLe: number;
}
export interface ParametresLocaux { annee: number; popTotale: number | null; intrantsAjoutes: Array<{ cle: string; programme: string; nom: string }> }
export interface CatalogueLocal { medicaments: MedicamentCatalogue[]; version: number; parDefaut: boolean; enAttente?: boolean }

type Op =
  | { opId: string; ordre: number; type: 'UPSERT'; periodeId: string; tableau: string; cle: string; donnees: any; baseVersion: number; envoye?: boolean; echecs?: number; refuse?: boolean; erreurRefus?: string }
  | { opId: string; ordre: number; type: 'DELETE'; periodeId: string; tableau: string; cle: string; baseVersion: number; envoye?: boolean; echecs?: number; refuse?: boolean; erreurRefus?: string }
  | { opId: string; ordre: number; type: 'PARAMETRES'; annee: number; popTotale?: number | null; ajouterIntrant?: { cle: string; programme: string; nom: string }; retirerIntrant?: string; envoye?: boolean; echecs?: number; refuse?: boolean; erreurRefus?: string }
  | { opId: string; ordre: number; type: 'CATALOGUE'; medicaments: MedicamentCatalogue[]; envoye?: boolean; echecs?: number; refuse?: boolean; erreurRefus?: string };

export interface Incident { id: string; date: number; type: 'CONFLIT' | 'REFUS'; periodeId?: string; tableau?: string; cle?: string; message: string; locale?: any; serveur?: any; vu?: boolean }

// ------------------------------------------------------------------ IndexedDB
let dbPromise: Promise<IDBDatabase> | null = null;
function ouvrir(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NOM, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore('snap', { keyPath: 'periodeId' });
      db.createObjectStore('queue', { keyPath: 'opId' }).createIndex('ordre', 'ordre');
      db.createObjectStore('incidents', { keyPath: 'id' });
      db.createObjectStore('meta', { keyPath: 'k' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => { dbPromise = null; reject(req.error); };
  });
  return dbPromise;
}
const attendre = <T,>(r: IDBRequest<T>) => new Promise<T>((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
const finTx = (tx: IDBTransaction) => new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); tx.onabort = () => rej(tx.error); });

let compteur = 0;
const nouvelId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${compteur++}`);
const prochainOrdre = () => Date.now() * 1000 + (compteur++ % 1000);

// ------------------------------------------------------------------ Évènements (écrans, badge de synchro)
type Ecouteur = () => void;
const ecouteurs = new Set<Ecouteur>();
export const surChangement = (f: Ecouteur) => { ecouteurs.add(f); return () => { ecouteurs.delete(f); }; };
const emettre = () => ecouteurs.forEach((f) => { try { f(); } catch { /* un écran défaillant ne doit pas bloquer les autres */ } });

export type EtatModules = 'IDLE' | 'SYNCING' | 'ERREUR';
let etat: EtatModules = 'IDLE';
let nbAttente = 0;   // opérations à envoyer (hors refusées)
let derniereErreur: string | null = null; // raison du dernier blocage de l'envoi (affichée dans l'écran de synchronisation)
let nbRefuses = 0;   // opérations refusées par le serveur : conservées localement, « Refusé / À vérifier »
export const lireEtatModules = () => ({ etat, nbAttente, nbRefuses, derniereErreur });

async function recompterAttente() {
  const db = await ouvrir();
  const ops = await attendre(db.transaction('queue').objectStore('queue').getAll()) as Op[];
  nbRefuses = ops.filter((o) => o.refuse).length;
  nbAttente = ops.length - nbRefuses;
}

// ------------------------------------------------------------------ Lecture locale
async function lire<T>(store: string, cle: IDBValidKey): Promise<T | undefined> {
  const db = await ouvrir();
  return attendre(db.transaction(store).objectStore(store).get(cle)) as Promise<T | undefined>;
}
export const chargerSnap = (periodeId: string) => lire<Snap>('snap', periodeId);
export async function chargerParametres(annee: number): Promise<ParametresLocaux> {
  return (await lire<{ k: string; v: ParametresLocaux }>('meta', `param_${annee}`))?.v ?? { annee, popTotale: null, intrantsAjoutes: [] };
}
export async function chargerCatalogue(): Promise<CatalogueLocal | null> {
  return (await lire<{ k: string; v: CatalogueLocal }>('meta', 'catalogue'))?.v ?? null;
}
export async function listerIncidents(): Promise<Incident[]> {
  const db = await ouvrir();
  const tous = await attendre(db.transaction('incidents').objectStore('incidents').getAll()) as Incident[];
  return tous.sort((a, b) => b.date - a.date);
}
export async function marquerIncidentsVus() {
  const db = await ouvrir();
  const tx = db.transaction('incidents', 'readwrite');
  const st = tx.objectStore('incidents');
  for (const i of await attendre(st.getAll()) as Incident[]) if (!i.vu) st.put({ ...i, vu: true });
  await finTx(tx); emettre();
}
// ------------------------------------------------------------------ Rafraîchissement depuis le serveur
interface ReponseSnapshot { periode: PeriodeSnap; saisies: LigneSnap[]; reports: ReportsModules; parametres: { popTotale: number | null; intrantsAjoutes: ParametresLocaux['intrantsAjoutes'] }; catalogue: CatalogueLocal; pevAnnee: Array<any | null> }

/** Télécharge l'état serveur d'une période et le fusionne : les lignes avec une opération en attente restent LOCALES. */
export async function rafraichir(periodeId: string): Promise<Snap | null> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return (await chargerSnap(periodeId)) ?? null;
  let rep: ReponseSnapshot;
  try { rep = await api.get<ReponseSnapshot>(`/modules/${periodeId}/snapshot`); } catch { return (await chargerSnap(periodeId)) ?? null; }
  const db = await ouvrir();
  const tx = db.transaction(['snap', 'queue', 'meta'], 'readwrite');
  const ops = await attendre(tx.objectStore('queue').getAll()) as Op[];
  const enAttente = new Set(ops.filter((o): o is Extract<Op, { tableau: string }> => 'tableau' in o && o.periodeId === periodeId).map((o) => `${o.tableau}|${o.cle}`));
  const ancien = await attendre(tx.objectStore('snap').get(periodeId)) as Snap | undefined;
  const locales = new Map((ancien?.saisies ?? []).filter((l) => enAttente.has(`${l.tableau}|${l.cle}`)).map((l) => [`${l.tableau}|${l.cle}`, l]));
  const saisies: LigneSnap[] = rep.saisies.filter((s) => !locales.has(`${s.tableau}|${s.cle}`)).map((s) => ({ tableau: s.tableau, cle: s.cle, donnees: s.donnees, version: s.version, id: s.id }));
  for (const l of locales.values()) saisies.push(l);
  const snap: Snap = { periodeId, periode: rep.periode, saisies, reports: rep.reports, pevAnnee: rep.pevAnnee, majLe: Date.now() };
  tx.objectStore('snap').put(snap);
  // Paramètres de l'année et catalogue : on ne les écrase pas s'ils ont une modification locale non synchronisée.
  if (!ops.some((o) => o.type === 'PARAMETRES' && o.annee === rep.periode.annee))
    tx.objectStore('meta').put({ k: `param_${rep.periode.annee}`, v: { annee: rep.periode.annee, ...rep.parametres } });
  if (!ops.some((o) => o.type === 'CATALOGUE')) tx.objectStore('meta').put({ k: 'catalogue', v: { ...rep.catalogue, enAttente: false } });
  await finTx(tx);
  emettre();
  return snap;
}

// ------------------------------------------------------------------ Écriture locale + file d'attente
async function modifierSnap(periodeId: string, f: (s: Snap) => void, store: IDBObjectStore) {
  const s = await attendre(store.get(periodeId)) as Snap | undefined;
  if (!s) throw new Error('Période non chargée sur cet appareil : ouvrez-la une première fois avec une connexion.');
  f(s);
  store.put(s);
}

/** Enregistre une ligne (création ou modification) : visible tout de suite, envoyée dès que possible. */
export async function ecrire(periodeId: string, tableau: string, cle: string, donnees: any): Promise<void> {
  const db = await ouvrir();
  const tx = db.transaction(['snap', 'queue'], 'readwrite');
  const q = tx.objectStore('queue');
  const ops = await attendre(q.getAll()) as Op[];
  let baseVersion = 0;
  await modifierSnap(periodeId, (s) => {
    const l = s.saisies.find((x) => x.tableau === tableau && x.cle === cle);
    if (l) { baseVersion = l.version; l.donnees = donnees; l.enAttente = true; } else s.saisies.push({ tableau, cle, donnees, version: 0, enAttente: true });
  }, tx.objectStore('snap'));
  const memeCle = ops.filter((o) => 'tableau' in o && o.periodeId === periodeId && o.tableau === tableau && o.cle === cle);
  const modifiable = memeCle.find((o) => o.type === 'UPSERT' && !o.envoye) as Extract<Op, { type: 'UPSERT' }> | undefined;
  const suppression = memeCle.find((o) => o.type === 'DELETE' && !o.envoye) as Extract<Op, { type: 'DELETE' }> | undefined;
  if (modifiable) q.put({ ...modifiable, donnees, refuse: false, erreurRefus: undefined }); // coalescence : on garde la version de base d'origine ; une ligne corrigée après un refus repart
  else {
    if (suppression) { q.delete(suppression.opId); baseVersion = suppression.baseVersion; } // supprimée puis recréée avant synchro
    q.put({ opId: nouvelId(), ordre: prochainOrdre(), type: 'UPSERT', periodeId, tableau, cle, donnees, baseVersion } satisfies Op);
  }
  await finTx(tx);
  await recompterAttente(); emettre(); planifier();
}

export async function supprimer(periodeId: string, tableau: string, cle: string): Promise<void> {
  const db = await ouvrir();
  const tx = db.transaction(['snap', 'queue'], 'readwrite');
  const q = tx.objectStore('queue');
  const ops = await attendre(q.getAll()) as Op[];
  let baseVersion = 0;
  await modifierSnap(periodeId, (s) => {
    const i = s.saisies.findIndex((x) => x.tableau === tableau && x.cle === cle);
    if (i >= 0) { baseVersion = s.saisies[i].version; s.saisies.splice(i, 1); }
  }, tx.objectStore('snap'));
  for (const o of ops) if ('tableau' in o && o.periodeId === periodeId && o.tableau === tableau && o.cle === cle && !o.envoye) q.delete(o.opId);
  if (baseVersion > 0) q.put({ opId: nouvelId(), ordre: prochainOrdre(), type: 'DELETE', periodeId, tableau, cle, baseVersion } satisfies Op);
  await finTx(tx);
  await recompterAttente(); emettre(); planifier();
}

export async function majParametres(annee: number, patch: { popTotale?: number | null; ajouterIntrant?: { cle: string; programme: string; nom: string }; retirerIntrant?: string }) {
  const db = await ouvrir();
  const tx = db.transaction(['meta', 'queue'], 'readwrite');
  const m = tx.objectStore('meta');
  const p = ((await attendre(m.get(`param_${annee}`)) as { v: ParametresLocaux } | undefined)?.v) ?? { annee, popTotale: null, intrantsAjoutes: [] };
  if (patch.popTotale !== undefined) p.popTotale = patch.popTotale;
  if (patch.ajouterIntrant) p.intrantsAjoutes = [...p.intrantsAjoutes.filter((x) => x.cle !== patch.ajouterIntrant!.cle), patch.ajouterIntrant];
  if (patch.retirerIntrant) p.intrantsAjoutes = p.intrantsAjoutes.filter((x) => x.cle !== patch.retirerIntrant);
  m.put({ k: `param_${annee}`, v: p });
  tx.objectStore('queue').put({ opId: nouvelId(), ordre: prochainOrdre(), type: 'PARAMETRES', annee, ...patch } satisfies Op);
  await finTx(tx);
  await recompterAttente(); emettre(); planifier();
}

export async function majCatalogue(medicaments: MedicamentCatalogue[]) {
  const db = await ouvrir();
  const tx = db.transaction(['meta', 'queue'], 'readwrite');
  const q = tx.objectStore('queue');
  const ancien = (await attendre(tx.objectStore('meta').get('catalogue')) as { v: CatalogueLocal } | undefined)?.v;
  tx.objectStore('meta').put({ k: 'catalogue', v: { medicaments, version: ancien?.version ?? 0, parDefaut: false, enAttente: true } });
  for (const o of await attendre(q.getAll()) as Op[]) if (o.type === 'CATALOGUE' && !o.envoye) q.delete(o.opId); // seule la dernière liste compte
  q.put({ opId: nouvelId(), ordre: prochainOrdre(), type: 'CATALOGUE', medicaments } satisfies Op);
  await finTx(tx);
  await recompterAttente(); emettre(); planifier();
}

// ------------------------------------------------------------------ Synchronisation séquentielle (UNE opération par requête)
let enCours = false;
let minuteur: ReturnType<typeof setTimeout> | null = null;
let tentatives = 0;
const MAX_ERREURS_SERVEUR_DE_SUITE = 3;

function planifier(delaiMs = 800) {
  if (minuteur) clearTimeout(minuteur);
  minuteur = setTimeout(() => { minuteur = null; void synchroniser(); }, delaiMs);
}

type Resultat = { opId: string; statut: 'OK' | 'CONFLIT' | 'REFUSE' | 'ERREUR'; version?: number; serveur?: { version: number; donnees: any }; erreurs?: string[] };

/**
 * Envoie la file des modules STRICTEMENT UNE OPÉRATION À LA FOIS (POST /modules/sync avec ops = [une seule opération]) : plus de lot de
 * 100, plus de requêtes groupées qui saturent un réseau 2G/3G.
 *  - REFUS métier (« SDU supérieur au stock restant », « Jours de rupture > 30 jours », « Refusé — COMMUNICATION »…) : l'opération reste
 *    dans IndexedDB, marquée « Refusé / À vérifier » (refuse = true), un incident est tracé, et on envoie IMMÉDIATEMENT la suivante.
 *    Elle n'est pas renvoyée automatiquement ; elle repart si l'utilisateur corrige la ligne, ou via « Forcer la synchronisation ».
 *  - PANNE (réseau, délai, 5xx, 429, session non renouvelable) : la passe s'arrête, tout est conservé, reprise espacée automatique.
 *  - ERREUR serveur sur UNE opération : elle est remise en file pour plus tard, sans bloquer les suivantes.
 */
export async function synchroniser(options: { reessayerRefuses?: boolean } = {}): Promise<void> {
  if (enCours || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
  enCours = true;
  let interrompue = false;
  let transitoire = false;
  const periodesTouchees = new Set<string>();
  const tentees = new Set<string>(); // opérations déjà essayées pendant CETTE passe (évite toute boucle infinie)
  let erreursServeurDeSuite = 0; // erreurs serveur (5xx) / locales consécutives : remis à 0 dès qu'une opération aboutit
  try {
    // Aucun envoi n'est en vol ici : un drapeau « envoyé » restant (onglet fermé en pleine synchro) est périmé → on renvoie (rejeu sans danger).
    // « Forcer la synchronisation » : les opérations refusées sont retentées une fois (le serveur a pu être corrigé entre-temps).
    { const db0 = await ouvrir(); const tx0 = db0.transaction('queue', 'readwrite');
      for (const o of await attendre(tx0.objectStore('queue').getAll()) as Op[]) {
        if (o.envoye || (options.reessayerRefuses && o.refuse)) tx0.objectStore('queue').put({ ...o, envoye: false, ...(options.reessayerRefuses ? { refuse: false, erreurRefus: undefined } : {}) });
      }
      await finTx(tx0); }
    await recompterAttente(); emettre();

    for (;;) {
      const db = await ouvrir();
      const aEnvoyer = (await attendre(db.transaction('queue').objectStore('queue').index('ordre').getAll()) as Op[]).filter((o) => !o.envoye && !o.refuse && !tentees.has(o.opId));
      if (!aEnvoyer.length || interrompue) break;
      const op = aEnvoyer[0]; // la plus ancienne d'abord (ordre de saisie conservé)
      tentees.add(op.opId);
      etat = 'SYNCING'; emettre();
      // On marque l'opération « envoyée » : une saisie faite pendant l'envoi créera une NOUVELLE opération (pas de coalescence en vol).
      { const tx = db.transaction('queue', 'readwrite'); tx.objectStore('queue').put({ ...op, envoye: true }); await finTx(tx); }
      let r: Resultat | undefined;
      try {
        const { envoye: _e, ordre: _o, refuse: _r, erreurRefus: _m, echecs: _c, ...reste } = op;
        // Nettoyage juste avant l'appel API : trim (clé, tableau, textes) et quantités vides / null / undefined → Number(valeur) || 0.
        // (debut, sdu, finSaisie, dtc1, dtc3… restent null : null = report / calcul automatique.)
        const rep = await api.post<{ resultats: Resultat[] }>('/modules/sync', { ops: [nettoyerOperationModule(reste)] });
        r = rep.resultats?.find((x) => x.opId === op.opId) ?? rep.resultats?.[0];
        erreursServeurDeSuite = 0; // le serveur a répondu
      } catch (err: any) {
        const st = err?.status as number | undefined;
        // 500 = plantage applicatif sur CETTE opération → refus définitif (traité plus bas). Les autres 5xx (502/503/504, 52x, démarrage à froid)
        // restent des pannes : l'envoi s'arrête et reprend plus tard.
        // Panne GLOBALE (réseau coupé, délai dépassé, 429, 401) → la passe s'arrête. Un 5xx isolé (502/503/504…) ou une erreur locale inattendue ne concerne
        // que CETTE opération : elle reste en file (essai compté) et on envoie IMMÉDIATEMENT la suivante ; la passe ne s'arrête qu'après
        // MAX_ERREURS_SERVEUR_DE_SUITE échecs consécutifs (serveur réellement hors service).
        const globale = st ? (st === 408 || st === 429 || st === 401) : (err?.reseau === true || (typeof navigator !== 'undefined' && !navigator.onLine));
        const isolee = !globale && (!st || (st >= 500 && st !== 500));
        if (isolee) {
          erreursServeurDeSuite += 1;
          derniereErreur = st ? `Serveur indisponible (code ${st}) sur une saisie : elle sera renvoyée, les suivantes continuent.` : (err?.message ?? 'Erreur inattendue sur une saisie : elle sera renvoyée.');
          if (erreursServeurDeSuite < MAX_ERREURS_SERVEUR_DE_SUITE) {
            { const tx = db.transaction('queue', 'readwrite'); tx.objectStore('queue').put({ ...op, envoye: false }); await finTx(tx); }
            transitoire = true;
            await recompterAttente(); emettre();
            continue; // opération suivante, sans figer la file
          }
        }
        const panne = globale || isolee;
        if (panne) {
          { const tx = db.transaction('queue', 'readwrite'); tx.objectStore('queue').put({ ...op, envoye: false }); await finTx(tx); }
          etat = 'ERREUR'; tentatives += 1; interrompue = true;
          derniereErreur = st === 401 ? 'Session expirée : reconnectez-vous pour reprendre l\'envoi.' : !st ? (err?.message ?? 'Réseau indisponible.') : `Serveur indisponible ou surchargé (code ${st}) : nouvel essai automatique.`;
          if (st === 401) break; // session non renouvelable : on garde tout, reprise après reconnexion
          planifier(st === 429 && err.retryAfter ? err.retryAfter * 1000 + 500 : Math.min(120_000, 2000 * 2 ** tentatives)); // 429 / réseau / 5xx : réessai espacé
          break;
        }
        // Autre 4xx (400, 403, 404, 413, 422, 423…) OU crash 500 : rejet définitif de cette seule opération → mention « Refusé » écrite dans
        // IndexedDB (refuse = true + incident), puis on envoie IMMÉDIATEMENT la suivante.
        const motif = err?.details?.length ? err.details.join(' / ')
          : st === 500 ? "Refusé (code 500) : erreur interne du serveur. La saisie est conservée sur l'appareil ; « Forcer la synchronisation » la renverra."
          : (err?.message ?? 'Saisie refusée par le serveur.');
        r = { opId: op.opId, statut: 'REFUSE', erreurs: [motif] };
      }
      const tx = db.transaction(['queue', 'snap', 'incidents'], 'readwrite');
      const q = tx.objectStore('queue'), sn = tx.objectStore('snap');
      if (!r || r.statut === 'ERREUR') {
        const echecs = (op.echecs ?? 0) + 1;
        const detail = (r?.erreurs ?? ['Aucune réponse du serveur pour cette opération.']).join(' ');
        if (echecs >= 3) {
          // Erreur serveur répétée sur CETTE opération : on la met de côté comme « Refusé / À vérifier » au lieu de la retenter sans fin.
          const message = `Erreur serveur répétée (${echecs} essais) : ${detail}`;
          q.put({ ...op, envoye: false, echecs, refuse: true, erreurRefus: message });
          tx.objectStore('incidents').put({
            id: `refus_${op.opId}`, date: Date.now(), type: 'REFUS', periodeId: 'tableau' in op ? op.periodeId : undefined,
            tableau: 'tableau' in op ? op.tableau : undefined, cle: 'tableau' in op ? op.cle : undefined, message,
            locale: op.type === 'UPSERT' ? op.donnees : undefined,
          } satisfies Incident);
        } else { q.put({ ...op, envoye: false, echecs }); transitoire = true; derniereErreur = detail; } // les suivantes partent quand même
        await finTx(tx);
      } else if (r.statut === 'REFUSE') {
        const message = (r.erreurs ?? ['Saisie refusée par le serveur.']).join(' ');
        // Conservée localement (rien n'est perdu), marquée refusée, sautée par les prochaines passes.
        q.put({ ...op, envoye: false, refuse: true, erreurRefus: message });
        tx.objectStore('incidents').put({
          id: `refus_${op.opId}`, date: Date.now(), type: 'REFUS', periodeId: 'tableau' in op ? op.periodeId : undefined,
          tableau: 'tableau' in op ? op.tableau : undefined, cle: 'tableau' in op ? op.cle : undefined, message,
          locale: op.type === 'UPSERT' ? op.donnees : undefined,
        } satisfies Incident);
        await finTx(tx);
      } else {
        q.delete(op.opId);
        if ('tableau' in op) {
          periodesTouchees.add(op.periodeId);
          const restantes = await attendre(q.getAll()) as Op[];
          const snap = await attendre(sn.get(op.periodeId)) as Snap | undefined;
          const ligne = snap?.saisies.find((l) => l.tableau === op.tableau && l.cle === op.cle);
          const suivantes = restantes.filter((o) => o.opId !== op.opId && 'tableau' in o && o.periodeId === op.periodeId && o.tableau === op.tableau && o.cle === op.cle);
          if (r.statut === 'OK') {
            if (op.type === 'UPSERT' && r.version !== undefined) {
              suivantes.forEach((o) => { if (o.type === 'UPSERT' || o.type === 'DELETE') q.put({ ...o, baseVersion: r!.version! }); });
              if (snap && ligne) { ligne.version = r.version; ligne.enAttente = suivantes.length > 0; sn.put(snap); }
            }
          } else { // CONFLIT : la version du serveur fait foi ; les opérations suivantes sur la même ligne reposent sur une base périmée.
            suivantes.forEach((o) => q.delete(o.opId));
            if (snap && r.serveur) {
              if (ligne) { ligne.donnees = r.serveur.donnees; ligne.version = r.serveur.version; ligne.enAttente = false; } else snap.saisies.push({ tableau: op.tableau, cle: op.cle, donnees: r.serveur.donnees, version: r.serveur.version });
              sn.put(snap);
            }
            tx.objectStore('incidents').put({
              id: nouvelId(), date: Date.now(), type: 'CONFLIT', periodeId: op.periodeId, tableau: op.tableau, cle: op.cle,
              message: 'Cette ligne a été modifiée ailleurs : la version du serveur a été conservée.', locale: op.type === 'UPSERT' ? op.donnees : undefined, serveur: r.serveur?.donnees,
            } satisfies Incident);
          }
        }
        await finTx(tx);
      }
      await recompterAttente(); emettre();
    }
    if (!interrompue && !transitoire) { etat = 'IDLE'; tentatives = 0; derniereErreur = null; }
    if (transitoire && !interrompue) { etat = 'ERREUR'; tentatives += 1; planifier(Math.min(120_000, 2000 * 2 ** tentatives)); }
    // Relit l'état serveur (versions, reports) UNE SEULE FOIS en fin de passe (et non après chaque opération : une requête lourde de moins par saisie).
    if (!interrompue) for (const id of periodesTouchees) await rafraichir(id);
  } finally {
    await recompterAttente();
    if (nbAttente === 0 && etat !== 'ERREUR') etat = 'IDLE';
    enCours = false; emettre();
  }
}

// ------------------------------------------------------------------ Démarrage / nettoyage
let demarre = false;
export function demarrerSynchroModules() {
  if (demarre || typeof window === 'undefined') return;
  demarre = true;
  void recompterAttente().then(() => { emettre(); if (nbAttente > 0) planifier(500); });
  window.addEventListener('online', () => { tentatives = 0; planifier(300); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') planifier(500); });
  setInterval(() => { if (nbAttente > 0) planifier(100); }, 30_000);
}

/** À la déconnexion : sur un poste partagé les données de santé ne doivent pas rester lisibles. Refuse s'il reste des saisies non envoyées. */
export async function effacerDonneesLocales(force = false): Promise<{ ok: boolean; enAttente: number }> {
  await recompterAttente();
  if (nbAttente + nbRefuses > 0 && !force) return { ok: false, enAttente: nbAttente + nbRefuses }; // les saisies refusées (à vérifier) sont aussi des données locales non envoyées
  const db = await ouvrir();
  const tx = db.transaction(['snap', 'queue', 'incidents', 'meta'], 'readwrite');
  for (const s of ['snap', 'queue', 'incidents', 'meta']) tx.objectStore(s).clear();
  await finTx(tx);
  nbAttente = 0; emettre();
  return { ok: true, enAttente: 0 };
}
